import { FirestoreCollections } from '@griply/types';
import {
  doc,
  DocumentReference,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { ID } from 'shared/types/id';
import { RoleOption } from 'shared/types/roles';
import { NewTagWithID, Tag } from 'shared/types/tag';
import { Timestamp } from 'shared/types/timestamp';

import { dateToFirestoreTimestamp } from './converters/date-to-firestore-timestamp';
import { newTagConverter } from './converters/tag';
import {
  BatchDocuments,
  getAuthentication,
  getCollection,
  getDatabase,
} from './helpers';

export const createTagsWithIds = async (
  tags: NewTagWithID[],
): Promise<{ tags: Tag[]; oldIdToNewIdMap: Record<ID, ID> }> => {
  // Get the currently authenticated user
  const user = (await getAuthentication()).currentUser;

  if (!user) {
    throw Error('unauthenticated');
  }

  const tagCollection = getCollection(FirestoreCollections.Tags);

  const { oldIdToNewIdMap, oldIdRefMap } = tags.reduce<{
    oldIdToNewIdMap: Record<ID, ID>;
    oldIdRefMap: Record<ID, DocumentReference>;
  }>(
    (acc, { id }) => {
      const ref = doc(tagCollection);
      acc.oldIdToNewIdMap[id] = ref.id;
      acc.oldIdRefMap[id] = ref;
      return acc;
    },
    { oldIdToNewIdMap: {}, oldIdRefMap: {} },
  );

  if (tags.length === 1) {
    // if only one goal is being created, we can use setDoc directly
    const tag = tags[0];
    const docRef = oldIdRefMap[tag.id];
    const data = {
      ...dateToFirestoreTimestamp(newTagConverter(tag)),
      id: docRef.id,
      createdAt: dateToFirestoreTimestamp(new Date()),
      updatedAt: serverTimestamp() as unknown as Timestamp,
      roles: { [RoleOption.Owner]: user.uid, [RoleOption.All]: [user.uid] },
    } as unknown as Tag;

    setDoc(docRef, data);

    return { tags: [data], oldIdToNewIdMap };
  }

  const batchDocuments = new BatchDocuments(getDatabase());

  const tagDocs = tags.map((tag) => {
    const docRef = oldIdRefMap[tag.id];

    return {
      ref: docRef,
      data: {
        ...dateToFirestoreTimestamp(newTagConverter(tag)),
        id: docRef.id,
        createdAt: dateToFirestoreTimestamp(new Date()),
        updatedAt: serverTimestamp() as unknown as Timestamp,
        roles: { [RoleOption.Owner]: user.uid, [RoleOption.All]: [user.uid] },
      } as unknown as Tag,
    };
  });

  tagDocs.forEach((docToAdd) => batchDocuments.addDocument(docToAdd));

  batchDocuments.commit();

  return {
    tags: tagDocs.map(({ data }) => data),
    oldIdToNewIdMap,
  };
};
